import React, { useState, useEffect } from 'react';
import { useFormContext, Controller, get, useWatch, Path } from 'react-hook-form';
import { Search, Paperclip, X, Trash2 } from 'lucide-react';

import { OrderSchemaType, ItemPath } from '../../../../order.schema';
import { useApiData } from '../../../../contexts/ApiDataContext';
import { useAttachmentHandler } from '../../../../Hooks/useAttachmentHandler';
import SteelOrdersInfiniteSelect from '../../../../components/SteelOrdersInfiniteSelect';
import { NumericInput } from '../../../../components/NumericInput';

import {
  FormGrid,
  FormGroup,
  Input,
  Label,
  Select,
  Separator,
  AttachmentList,
  AttachmentListItem,
  RemoveAttachmentButton,
  FormErrorMessage,
  AttachmentInputContainer,
  AttachmentTrigger,
  AttachmentPlaceholder,
  TrashIconContainer,
} from '../../../../style';

interface ResourceSectionProps {
  itemPath: ItemPath;
}

const ResourceSection: React.FC<ResourceSectionProps> = ({ itemPath }) => {
  const { register, control, formState: { errors }, setValue, watch } = useFormContext<OrderSchemaType>();

  const [selectedResources, setSelectedResources] = useState<{ id: number; name: string }[]>([]);
  const [selectedResourceCategory, setSelectedResourceCategory] = useState<{ id: number; name: string }[]>([]);
  const [selectedResourceType, setSelectedResourceType] = useState<{ id: number; name: string }[]>([]);
  const [selectedSupplier, setSelectedSupplier] = useState<{ id: number; name: string }[]>([]);
  
  const API_BASE_URL = `${process.env.REACT_APP_GATEWAY_BASE_URL}/steel-orders`;

  const {
    attachments,
    attachmentError,
    fileInputRef,
    handleFileSelectClick,
    handleFileChange,
    handleRemoveAttachment,
    handleRemoveAllAttachments,
  } = useAttachmentHandler({ fieldPath: `${itemPath}.resource.anexos` });

  const resourcePath = `${itemPath}.resource` as `items.${number}.resource`;

  const isSubItem = itemPath.includes('.subItems.');

	useEffect(() => {
		const resource = watch(`${resourcePath}.insumo`);
		const existingResourceId = watch((`${itemPath}.resourceId`) as any) as number | undefined;
    if (resource && typeof resource === 'string' && selectedResources.length === 0) {
			setValue(`${resourcePath}.insumo`, resource);
			if (existingResourceId === undefined || existingResourceId === null) {
				setValue((`${itemPath}.resourceId`) as any, undefined, { shouldDirty: true, shouldValidate: false });
			}
      setSelectedResourceCategory([]);
      setSelectedResourceType([]);
      setSelectedSupplier([]);
      setValue((`${itemPath}.resourceCategoryId`) as any, undefined, { shouldDirty: true, shouldValidate: false });
      setValue((`${itemPath}.resourceTypeId`) as any, undefined, { shouldDirty: true, shouldValidate: false });
      setValue((`${resourcePath}.categoria`) as any, '', { shouldDirty: true, shouldValidate: false });
      setValue((`${resourcePath}.tipo`) as any, '', { shouldDirty: true, shouldValidate: false });
      setValue((`${itemPath}.resourceSupplierCompanyId`) as any, undefined, { shouldDirty: true, shouldValidate: false });
      setValue((`${resourcePath}.fornecedor`) as any, '', { shouldDirty: true, shouldValidate: false });
		} else if (selectedResources.length > 0) {
			setValue(`${resourcePath}.insumo`, selectedResources[0].name);
			setValue((`${itemPath}.resourceId`) as any, selectedResources[0].id, { shouldDirty: true, shouldValidate: false });
      setSelectedResourceCategory([]);
      setSelectedResourceType([]);
      setSelectedSupplier([]);
      setValue((`${itemPath}.resourceCategoryId`) as any, undefined, { shouldDirty: true, shouldValidate: false });
      setValue((`${itemPath}.resourceTypeId`) as any, undefined, { shouldDirty: true, shouldValidate: false });
      setValue((`${resourcePath}.categoria`) as any, '', { shouldDirty: true, shouldValidate: false });
      setValue((`${resourcePath}.tipo`) as any, '', { shouldDirty: true, shouldValidate: false });
      setValue((`${itemPath}.resourceSupplierCompanyId`) as any, undefined, { shouldDirty: true, shouldValidate: false });
      setValue((`${resourcePath}.fornecedor`) as any, '', { shouldDirty: true, shouldValidate: false });
		} else {
			if (existingResourceId === undefined || existingResourceId === null) {
				setValue(`${resourcePath}.insumo`, '');
				setValue((`${itemPath}.resourceId`) as any, undefined, { shouldDirty: true, shouldValidate: false });
			}
      setSelectedResourceCategory([]);
      setSelectedResourceType([]);
      setSelectedSupplier([]);
      setValue((`${itemPath}.resourceCategoryId`) as any, undefined, { shouldDirty: true, shouldValidate: false });
      setValue((`${itemPath}.resourceTypeId`) as any, undefined, { shouldDirty: true, shouldValidate: false });
      setValue((`${resourcePath}.categoria`) as any, '', { shouldDirty: true, shouldValidate: false });
      setValue((`${resourcePath}.tipo`) as any, '', { shouldDirty: true, shouldValidate: false });
      setValue((`${itemPath}.resourceSupplierCompanyId`) as any, undefined, { shouldDirty: true, shouldValidate: false });
      setValue((`${resourcePath}.fornecedor`) as any, '', { shouldDirty: true, shouldValidate: false });
		}
	}, [selectedResources, setValue, resourcePath, watch, itemPath]);

  useEffect(() => {
    const existingResourceId = watch((`${itemPath}.resourceId`) as any) as number | undefined;
    const existingResourceName = watch(`${resourcePath}.insumo`) as string | undefined;
    const ecId = watch((`${itemPath}.resourceCategoryId`) as any) as number | undefined;
    const etId = watch((`${itemPath}.resourceTypeId`) as any) as number | undefined;
    const esId = watch((`${itemPath}.resourceSupplierCompanyId`) as any) as number | undefined;
    if (existingResourceId && selectedResources.length === 0) {
      setSelectedResources([{ id: existingResourceId, name: existingResourceName || '' }]);
    }
    if (ecId && selectedResourceCategory.length === 0) {
      setSelectedResourceCategory([{ id: ecId, name: watch(`${resourcePath}.categoria`) || '' }]);
    }
    if (etId && selectedResourceType.length === 0) {
      setSelectedResourceType([{ id: etId, name: watch(`${resourcePath}.tipo`) || '' }]);
    }
    if (esId && selectedSupplier.length === 0) {
      setSelectedSupplier([{ id: esId, name: watch(`${resourcePath}.fornecedor`) || '' }]);
    }
  }, [watch, itemPath, resourcePath, selectedResources.length, selectedResourceCategory.length, selectedResourceType.length, selectedSupplier.length]);

  const watchedResourceId = useWatch({ control, name: (`${itemPath}.resourceId` as any) });
  const watchedResourceName = useWatch({ control, name: (`${resourcePath}.insumo` as any) });
  const watchedResCatId = useWatch({ control, name: (`${itemPath}.resourceCategoryId` as any) });
  const watchedResCatName = useWatch({ control, name: (`${resourcePath}.categoria` as any) });
  const watchedResTypeId = useWatch({ control, name: (`${itemPath}.resourceTypeId` as any) });
  const watchedResTypeName = useWatch({ control, name: (`${resourcePath}.tipo` as any) });
  const watchedResSupplierId = useWatch({ control, name: (`${itemPath}.resourceSupplierCompanyId` as any) });
  const watchedResSupplierName = useWatch({ control, name: (`${resourcePath}.fornecedor` as any) });

  useEffect(() => {
    if (watchedResourceId && selectedResources.length === 0) setSelectedResources([{ id: Number(watchedResourceId), name: String(watchedResourceName || '') }]);
    if (watchedResCatId && selectedResourceCategory.length === 0) setSelectedResourceCategory([{ id: Number(watchedResCatId), name: String(watchedResCatName || '') }]);
    if (watchedResTypeId && selectedResourceType.length === 0) setSelectedResourceType([{ id: Number(watchedResTypeId), name: String(watchedResTypeName || '') }]);
    if (watchedResSupplierId && selectedSupplier.length === 0) setSelectedSupplier([{ id: Number(watchedResSupplierId), name: String(watchedResSupplierName || '') }]);
    
    const fetchMissingNames = async () => {
      try {
        if (watchedResCatId && !watchedResCatName) {
          const res = await fetch(`${API_BASE_URL}/select-in-order/resource-categories`);
          const cats = await res.json();
          const cat = (Array.isArray(cats) ? cats : []).find((c: any) => Number(c.id) === Number(watchedResCatId));
          if (cat?.name) {
            setValue((`${resourcePath}.categoria`) as any, String(cat.name), { shouldDirty: false, shouldValidate: false });
            if (selectedResourceCategory.length === 0) setSelectedResourceCategory([{ id: Number(watchedResCatId), name: String(cat.name) }]);
          }
        }
        if (watchedResTypeId && !watchedResTypeName) {
          const res = await fetch(`${API_BASE_URL}/select-in-order/resource-types`);
          const types = await res.json();
          const tp = (Array.isArray(types) ? types : []).find((t: any) => Number(t.id) === Number(watchedResTypeId));
          if (tp?.name) {
            setValue((`${resourcePath}.tipo`) as any, String(tp.name), { shouldDirty: false, shouldValidate: false });
            if (selectedResourceType.length === 0) setSelectedResourceType([{ id: Number(watchedResTypeId), name: String(tp.name) }]);
          }
        }
        if (watchedResSupplierId && !watchedResSupplierName) {
          let supplierName: string | undefined = undefined;
          if (watchedResourceId) {
            const params = new URLSearchParams({ resourceId: String(watchedResourceId), limit: '100' });
            const res = await fetch(`${API_BASE_URL}/select-in-order/resource-suppliers?${params.toString()}`);
            const data = await res.json();
            const list = Array.isArray(data?.suppliers) ? data.suppliers : Array.isArray(data) ? data : [];
            const found = list.find((s: any) => Number(s.id) === Number(watchedResSupplierId));
            supplierName = found?.name || found?.companyName;
          }
          if (!supplierName) {
            const resAll = await fetch(`${API_BASE_URL}/select-in-order/suppliers`);
            const dataAll = await resAll.json();
            const listAll = Array.isArray(dataAll?.suppliers) ? dataAll.suppliers : Array.isArray(dataAll) ? dataAll : [];
            const foundAll = listAll.find((s: any) => Number(s.id) === Number(watchedResSupplierId));
            supplierName = foundAll?.name || foundAll?.companyName;
          }
          if (supplierName) {
            setValue((`${resourcePath}.fornecedor`) as any, String(supplierName), { shouldDirty: false, shouldValidate: false });
            if (selectedSupplier.length === 0) setSelectedSupplier([{ id: Number(watchedResSupplierId), name: String(supplierName) }]);
          }
        }
      } catch (e) { void e; }
    };
    fetchMissingNames();
  }, [watchedResourceId, watchedResourceName, watchedResCatId, watchedResCatName, watchedResTypeId, watchedResTypeName, watchedResSupplierId, watchedResSupplierName, selectedResources.length, selectedResourceCategory.length, selectedResourceType.length, selectedSupplier.length]);

  useEffect(() => {
    if (selectedResourceCategory.length > 0) {
      setValue(`${resourcePath}.categoria`, selectedResourceCategory[0].name);
      setValue((`${itemPath}.resourceCategoryId`) as any, selectedResourceCategory[0].id, { shouldDirty: true, shouldValidate: false });
    }
  }, [selectedResourceCategory, setValue, resourcePath]);

  useEffect(() => {
    if (selectedResourceType.length > 0) {
      setValue(`${resourcePath}.tipo`, selectedResourceType[0].name);
      setValue((`${itemPath}.resourceTypeId`) as any, selectedResourceType[0].id, { shouldDirty: true, shouldValidate: false });
    }
  }, [selectedResourceType, setValue, resourcePath]);

  useEffect(() => {
    if (selectedSupplier.length > 0) {
      setValue(`${resourcePath}.fornecedor`, selectedSupplier[0].name);
      setValue((`${itemPath}.resourceSupplierCompanyId`) as any, selectedSupplier[0].id, { shouldDirty: true, shouldValidate: false });
    }
  }, [selectedSupplier, setValue, resourcePath]);

  useEffect(() => {
    if (!isSubItem) return;
    const qty = Number(watch(`${resourcePath}.quantidade`));
    if (!isNaN(qty)) {
      setValue((`${itemPath}.quantity`) as any, qty, { shouldDirty: true, shouldValidate: false });
    }
  }, [watch(`${resourcePath}.quantidade`), isSubItem]);

  return (
    <>
      <FormGrid columns={3} style={{ marginTop: '16px' }}>
        <SteelOrdersInfiniteSelect
          label="Insumo"
          items={selectedResources}
          setItems={setSelectedResources}
          endpoint="resources"
          placeholder="Procure por nome ou código"
          onCleared={() => {
            setValue((`${itemPath}.resourceId`) as any, undefined, { shouldDirty: true, shouldValidate: false });
            setValue((`${resourcePath}.insumo`) as any, '', { shouldDirty: true, shouldValidate: false });
            setSelectedResourceCategory([]);
            setSelectedResourceType([]);
            setSelectedSupplier([]);
          }}
        />
        <SteelOrdersInfiniteSelect
          label="Categoria"
          items={selectedResourceCategory}
          setItems={setSelectedResourceCategory}
          endpoint="resource-categories"
          placeholder="Selecione a categoria"
          additionalParams={selectedResources[0]?.id ? { resourceId: selectedResources[0].id } : {}}
          searchPlaceholder={selectedResources[0]?.id ? 'Nenhuma categoria encontrada' : 'Selecione um insumo primeiro'}
          isDisabled={!selectedResources[0]?.id}
          key={selectedResources[0]?.id ? `resource-categories-${selectedResources[0].id}` : 'resource-categories-disabled'}
          onCleared={() => {
            setValue((`${itemPath}.resourceCategoryId`) as any, undefined, { shouldDirty: true, shouldValidate: false });
            setValue((`${resourcePath}.categoria`) as any, '', { shouldDirty: true, shouldValidate: false });
          }}
        />
        <SteelOrdersInfiniteSelect
          label="Tipo"
          items={selectedResourceType}
          setItems={setSelectedResourceType}
          endpoint="resource-types"
          placeholder="Selecione o tipo"
          additionalParams={selectedResources[0]?.id && selectedResourceCategory[0]?.id ? { resourceId: selectedResources[0].id, categoryId: selectedResourceCategory[0].id } : {}}
          searchPlaceholder={selectedResources[0]?.id ? (selectedResourceCategory[0]?.id ? 'Nenhum tipo encontrado' : 'Selecione uma categoria primeiro') : 'Selecione um insumo primeiro'}
          isDisabled={!selectedResources[0]?.id || !selectedResourceCategory[0]?.id}
          key={selectedResources[0]?.id && selectedResourceCategory[0]?.id ? `resource-types-${selectedResources[0].id}-${selectedResourceCategory[0].id}` : 'resource-types-disabled'}
          onCleared={() => {
            setValue((`${itemPath}.resourceTypeId`) as any, undefined, { shouldDirty: true, shouldValidate: false });
            setValue((`${resourcePath}.tipo`) as any, '', { shouldDirty: true, shouldValidate: false });
          }}
        />
      </FormGrid>

      <FormGrid columns={3} style={{ marginTop: '16px' }}>
        <SteelOrdersInfiniteSelect
          label="Fornecedor"
          items={selectedSupplier}
          setItems={setSelectedSupplier}
          endpoint="resource-suppliers"
          placeholder="Procure por nome ou código"
          searchPlaceholder={selectedResources[0]?.id ? 'Nenhum fornecedor encontrado' : 'Selecione um insumo primeiro'}
          additionalParams={selectedResources[0]?.id ? { resourceId: selectedResources[0].id } : {}}
          key={selectedResources[0]?.id || 'resource-supplier'}
          isDisabled={!selectedResources[0]?.id}
          onCleared={() => {
            setValue((`${itemPath}.resourceSupplierCompanyId`) as any, undefined, { shouldDirty: true, shouldValidate: false });
            setValue((`${resourcePath}.fornecedor`) as any, '', { shouldDirty: true, shouldValidate: false });
          }}
        />
        {isSubItem && (
          <FormGroup>
            <Label htmlFor={`${resourcePath}.quantidade`}>Quantidade</Label>
            <NumericInput
              id={`${resourcePath}.quantidade`}
              name={`${resourcePath}.quantidade`}
              value={(watch(`${resourcePath}.quantidade` as any) as any) || ''}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setValue(`${resourcePath}.quantidade` as any, e.target.value, { shouldDirty: true })}
              onBlur={() => {}}
              hasError={!!get(errors, `${resourcePath}.quantidade`)}
            />
          </FormGroup>
        )}
        <FormGroup>
          <Label>Arquivo</Label>
          <AttachmentInputContainer>
            <AttachmentTrigger onClick={handleFileSelectClick} hasError={!!attachmentError}>
              <Paperclip size={16} />
              <AttachmentPlaceholder>Clique para anexar arquivo</AttachmentPlaceholder>
            </AttachmentTrigger>
            {attachments.length > 0 && (
              <TrashIconContainer
                as="button"
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleRemoveAllAttachments();
                }}
              >
                <Trash2 size={16} />
              </TrashIconContainer>
            )}
          </AttachmentInputContainer>
          {attachments.length > 0 && (
            <AttachmentList>
              {attachments.map((file, index) => (
                <AttachmentListItem key={index}>
                  {file.name}
                  <RemoveAttachmentButton
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleRemoveAttachment(index);
                    }}
                  >
                    <X size={12} />
                  </RemoveAttachmentButton>
                </AttachmentListItem>
              ))}
            </AttachmentList>
          )}
          <input
            type="file"
            multiple
            ref={fileInputRef}
            onChange={handleFileChange}
            style={{ display: 'none' }}
            accept=".pdf,.xml,.doc,.docx,.xls,.xlsx,.jpeg,.jpg,.png"
          />
          {attachmentError && <FormErrorMessage>{attachmentError}</FormErrorMessage>}
        </FormGroup>
      </FormGrid>
    </>
  );
};

export default ResourceSection;
